import React, { useEffect, useState } from "react";
import List, { STATE } from "../../../List/index";
import SecondaryPagination from "../../../SecondaryPagination/SecondaryPagination";

import { ReactComponent as ManagePrioritySvg } from "../../../../assets/admin/icons/manage-priority.svg";
import { ReactComponent as PreviewPageSvg } from "../../../../assets/admin/icons/preview-page.svg";
import { ReactComponent as AddSvg } from "../../../../assets/admin/icons/add-color.svg";
import { ReactComponent as EyeSvg } from "../../../../assets/admin/icons/eye.svg";
import { ReactComponent as TrashSvg } from "../../../../assets/admin/icons/trash.svg";
import heroBannerImg from "../../../../assets/temp/hero-banner.png";

import SearchBox from "../../SearchBox";
import useUsers from "../../../../hooks/useUsers";
import { endOfDay, format, startOfDay } from "date-fns";
import useLandingPage from "../../../../hooks/useLandingPage";
import View from "../View";
import ManagePriorities from "../ManagePriorities";
import useWorks from "../../../../hooks/useWorks";

const columns = [
  {
    display: "Project Name",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Owner",
    key: "owner",
    textAlign: "left",
    maxWidth: 150,
  },
  {
    display: "Start date",
    key: "effectiveDate",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "End date",
    key: "dueDate",
    textAlign: "left",
    maxWidth: 80,
  },
  {
    display: "Status",
    key: "status",
    textAlign: "left",
    maxWidth: 100,
  },
  {
    display: "",
    key: "actions",
    textAlign: "left",
    maxWidth: 50,
  },
];

const TrendingNow = () => {
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [heroBanners, setHeroBanners] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [enableActions, setEnableActions] = useState(false);
  const [selections, setSelections] = useState([]);

  // * Toggle states
  const [toggleAdd, setToggleAdd] = useState(false);
  const [toggleManagePriorities, setToggleManagePriorities] = useState(false);
  const [toggleView, setToggleView] = useState(false);
  const [toggleDelete, setToggleDelete] = useState(false);
  const [selected, setSelected] = useState(undefined);

  const { queryUsers } = useUsers();
  const { queryLandingPageConfigs, deleteLandingPage } = useLandingPage();
  const { queryWorks } = useWorks();

  useEffect(() => {
    fetchData();
    return () => {};
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter, limit, page]);

  useEffect(() => {
    setEnableActions(selections.length > 0);
  }, [selections]);

  // * Fetch data
  const fetchData = async () => {
    const { data, _metadata } = await queryLandingPageConfigs(
      { ...filter, section: 2, deleted: false },
      page,
      limit
    );

    let _works = [];

    let _heroBanners = data.map(
      ({ _id, effectiveDate, dueDate, active, createdBy, work }) => {
        const _effectiveDate = format(new Date(effectiveDate), "dd/MM/yyyy");
        const _dueDate = format(new Date(dueDate), "dd/MM/yyyy");

        const _inUse =
          new Date() >= startOfDay(new Date(effectiveDate)) &&
          new Date() <= endOfDay(new Date(dueDate));

        _works.push(work);

        return {
          _id,
          workId: work,
          name: work,
          effectiveDate: _effectiveDate,
          dueDate: _dueDate,
          status: _inUse && (
            <div className="admin-lp-hb-item-status">
              <label className="admin-lp-hb-item-status__label">In use</label>
            </div>
          ),
          owner: createdBy,
          actions: <></>,
        };
      }
    );

    // * Get Users and Works
    // TODO: Query works when API is available
    const { data: workDatas } = await queryWorks(
      {
        _id: { $in: _works },
      },
      0,
      _works.length
    );

    // * Get creators from works
    const _createdBys = workDatas.map((w) => w.createdBy);

    const { data: userDatas } = await queryUsers(
      {
        _id: { $in: _createdBys },
      },
      0,
      _createdBys.length
    );

    // * Map User and Work to Hero Banners
    _heroBanners = _heroBanners.map((hb) => {
      const work = workDatas.find((w) => w._id === hb.workId);
      const user = userDatas.find((u) => u._id === work.createdBy);

      if (user) {
        hb.owner = (
          <div className="admin-lp-hb-lists-item-owner">
            <div className="admin-lp-hb-lists-item-owner__name">
              {`${user.firstname} ${user.surname}`}
            </div>
            <div className="admin-lp-hb-lists-item-owner__email">
              {user.email}
            </div>
          </div>
        );
      }

      if (work) {
        hb.name = (
          <div className="admin-lp-hb-lists-item-name">
            <div className="admin-lp-hb-lists-item-name-banner">
              <img
                className="admin-lp-hb-lists-item-name-banner__image"
                src={work.banner}
              />
            </div>
            <div className="admin-lp-hb-lists-item-name__title">
              {work.name}
            </div>
          </div>
        );
      }

      hb.actions = (
        <div className="admin-lp-hb-item-actions">
          <div
            className="admin-lp-hb-item-actions-action"
            onClick={() => handleView(hb._id)}
          >
            <EyeSvg />
          </div>
          <div
            className="admin-lp-hb-item-actions-action"
            onClick={() => handleDelete(hb._id, work?.name)}
          >
            <TrashSvg />
          </div>
        </div>
      );

      return hb;
    });

    setHeroBanners(_heroBanners);
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const handleSelected = (row) => {};

  const handleOnPageChange = (page) => setPage(page);

  const handleOnLimitChange = (limit) => setLimit(limit);

  const handleSearchChange = (value) => {
    if (!value) {
      setFilter({});
      return;
    }

    setFilter({
      $or: [
        {
          no: isNaN(value) ? 0 : +value,
        },
        {
          email: { $regex: value },
        },
      ],
    });
  };

  const handleSearch = (values) => {
    const { search } = values;
  };

  const handleManagePriority = () => {
    setToggleManagePriorities(true);
  };

  const handlePreviewPage = () => {};

  const handleAdd = () => {
    setToggleAdd(true);
  };

  const handleView = async (id) => {
    if (toggleView) {
      setSelected(undefined);
    }

    setSelected(id);
    setToggleView(true);
  };

  const handleDelete = async (id, name) => {
    if (toggleView) {
      setSelected(undefined);
    }

    setToggleDelete(!toggleDelete);
    setSelected(id);

    const confirm = window.confirm(`Delete ${name}?`);

    if (confirm) {
      const result = await deleteLandingPage(id);
      fetchData();
    }
  };

  const handleViewSuccess = () => {
    setToggleAdd(false);
    setToggleView(false);

    // * Refetch data
    fetchData();
  };

  const handleViewClose = () => {
    setSelected(undefined);
    setToggleAdd(false);
    setToggleView(false);
  };

  return (
    <>
      <div className="admin-lp-hb">
        <div className="admin-lp-hb-header">
          <SearchBox
            placeholder="Work name or Owner"
            onChange={handleSearchChange}
          />
          <div className="admin-lp-hb-header-actions">
            <div
              className={`admin-lp-hb-header-actions-action`}
              style={{ color: "#0098FF" }}
              onClick={handleAdd}
            >
              <div className="admin-lp-hb-header-actions-action__icon">
                <AddSvg />
              </div>
              <div className="admin-lp-hb-header-actions-action__text">Add</div>
            </div>
            <div
              className={`admin-lp-hb-header-actions-action`}
              style={{ color: "#0098FF" }}
              onClick={handleManagePriority}
            >
              <div className="admin-lp-hb-header-actions-action__icon">
                <ManagePrioritySvg />
              </div>
              <div className="admin-lp-hb-header-actions-action__text">
                Manage Priorities
              </div>
            </div>
            <div
              className={`admin-lp-hb-header-actions-action`}
              style={{ color: "#0098FF" }}
              onClick={handlePreviewPage}
            >
              <div className="admin-lp-hb-header-actions-action__icon">
                <PreviewPageSvg />
              </div>
              <div className="admin-lp-hb-header-actions-action__text">
                Preview
              </div>
            </div>
          </div>
        </div>
        <List
          className="admin-lp-hb-lists"
          contentState={dataState}
          columns={columns}
          dataSource={heroBanners}
          onRecordClick={(e) => handleSelected(e)}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
      {(toggleAdd || toggleView) && (
        <View
          section={2}
          editing={toggleView && selected}
          onClose={handleViewClose}
          onSuccess={handleViewSuccess}
        />
      )}
      {toggleManagePriorities && (
        <ManagePriorities
          section={2}
          onClose={() => setToggleManagePriorities(false)}
        />
      )}
    </>
  );
};

TrendingNow.propTypes = {};

export default TrendingNow;
